<template>
  <!--
    Renders the finalized Data Processing Addendum inline. Source markdown
    lives at /legal/dpa.md (copied from src/assets/legal/ at build time).
    Mirrors the rendering pattern in terms_and_conditions.vue.
  -->
  <div class="legal-page">
    <div class="legal-page-card">
      <div class="legal-page-header">
        <h1 class="legal-page-title">Data Processing Addendum</h1>
        <p class="legal-page-meta">
          Version <code>{{ dpa_version }}</code>
          &middot; Effective {{ effective_date }}
        </p>
      </div>

      <div v-if="loading" class="legal-page-loading">Loading…</div>
      <div v-else-if="error" class="legal-page-error">
        {{ error }}
        <p style="margin-top:8px;font-size:13px;">
          You can also read the current DPA at
          <a href="https://kiey.com/dpa.html">kiey.com/dpa.html</a>.
        </p>
      </div>
      <article
        v-else
        class="legal-page-body"
        v-html="rendered_html"
      ></article>
    </div>
  </div>
</template>

<script>
import config from "@/config/constants.js";
import { renderLegalMarkdown } from "@/util/legal_md.js";

export default {
  name: "DataProcessingAddendum",
  data() {
    return {
      loading: true,
      error: "",
      rendered_html: "",
      // The DPA is published at the same version cadence as the ToS for
      // now — both are tracked together.
      dpa_version: config.tos_current_version,
      effective_date: "May 3, 2026",
    };
  },
  mounted() {
    fetch("/legal/dpa.md", { cache: "no-store" })
      .then((res) => {
        if (!res.ok) throw new Error("HTTP " + res.status);
        return res.text();
      })
      .then((text) => {
        this.rendered_html = renderLegalMarkdown(text);
        this.loading = false;
      })
      .catch((err) => {
        console.error("[dpa] failed to load:", err);
        this.error = "Could not load the Data Processing Addendum.";
        this.loading = false;
      });
  },
};
</script>

<style scoped>
.legal-page {
  min-height: 100vh;
  background: #f5f7fb;
  padding: 32px 16px;
  box-sizing: border-box;
}
.legal-page-card {
  max-width: 920px;
  margin: 0 auto;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(6, 29, 71, 0.06);
  padding: 36px 40px;
  box-sizing: border-box;
}
.legal-page-header {
  border-bottom: 1px solid #e4e8f1;
  padding-bottom: 18px;
  margin-bottom: 24px;
}
.legal-page-title {
  color: #061d47;
  font-family: "Roboto-Medium", Arial, sans-serif;
  font-size: 28px;
  font-weight: 600;
  margin: 0 0 6px;
}
.legal-page-meta {
  color: #6a7388;
  font-size: 13px;
  margin: 0;
}
.legal-page-meta code {
  background: #eef0f5;
  border-radius: 4px;
  padding: 1px 6px;
  font-size: 12px;
}
.legal-page-loading,
.legal-page-error {
  color: #6a7388;
  font-size: 14px;
  padding: 20px 0;
}
.legal-page-error { color: #c0392b; }
.legal-page-body {
  color: #2d2d2d;
  line-height: 1.65;
  font-size: 15px;
}
.legal-page-body >>> h2 {
  color: #061d47;
  font-size: 20px;
  font-weight: 700;
  margin: 32px 0 12px;
}
.legal-page-body >>> h3 {
  color: #061d47;
  font-size: 16px;
  font-weight: 700;
  margin: 22px 0 10px;
}
.legal-page-body >>> p { margin: 0 0 12px; }
.legal-page-body >>> ul,
.legal-page-body >>> ol { margin: 8px 0 16px; padding-left: 22px; }
.legal-page-body >>> li { margin-bottom: 6px; }
.legal-page-body >>> strong { color: #061d47; font-weight: 700; }
.legal-page-body >>> a { color: #29abe2; text-decoration: none; }
.legal-page-body >>> a:hover { text-decoration: underline; }
.legal-page-body >>> blockquote {
  margin: 12px 0 16px;
  padding: 10px 14px;
  border-left: 3px solid #29488d;
  background: rgba(41,73,141,0.05);
  border-radius: 0 4px 4px 0;
}
.legal-page-body >>> code {
  background: #eef0f5;
  border-radius: 4px;
  padding: 1px 6px;
  font-size: 0.9em;
  font-family: "SF Mono", Menlo, Monaco, Consolas, monospace;
}
.legal-page-body >>> hr {
  border: 0;
  border-top: 1px solid #e4e8f1;
  margin: 28px 0;
}
.legal-page-body >>> .legal-table-wrap { overflow-x: auto; margin: 12px 0 18px; }
.legal-page-body >>> .legal-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}
.legal-page-body >>> .legal-table th,
.legal-page-body >>> .legal-table td {
  border: 1px solid #e4e8f1;
  padding: 8px 10px;
  vertical-align: top;
  text-align: left;
}
.legal-page-body >>> .legal-table th {
  background: #f0f3f9;
  color: #061d47;
  font-weight: 700;
}

@media (max-width: 720px) {
  .legal-page-card { padding: 24px 18px; }
  .legal-page-title { font-size: 22px; }
}
</style>
